<template>
    <v-container fluid class="pa-0 pb-8">
        <v-container class="container-custom" v-if="!updateCourseStatus">
            <Breadcrumbs :title="$t('modules.coursesPrice.update.breadcrumbs.title')"
                :description="$t('modules.coursesPrice.update.breadcrumbs.description')"></Breadcrumbs>
            <v-row justify="center">
                <v-col class="viewSpaces" sm="12">
                    <OutlinedCard
                        :title="$t('modules.coursesPrice.update.section_product.title')"
                        :subtitle="$t('modules.coursesPrice.update.section_product.subtitle')"
                        :switchLabel="formattedCoursesStatus"
                        switcher
                        :switchValue="status"
                        @switchChange="status = !status"
                        class="mb-6"
                    >
                        <v-row>
                            <v-col sm="4" class="py-0">
                            <v-select
                            :error-messages="$v.selectedBillingProduct.$dirty && $v.selectedBillingProduct.$invalid ? $t('modules.coursesPrice.update.section_product.product_error'):''"
                            :items="filteredBillingProducts"
                            @blur="$v.selectedBillingProduct.$touch()"
                            class="mt-3"
                            item-text="name"
                            :label="$t('modules.coursesPrice.update.section_product.product')"
                            outlined
                            return-object
                            v-model="$v.selectedBillingProduct.$model"
                        />
                        </v-col>
                        </v-row>
                    </OutlinedCard>
                    <OutlinedCard :title="$t('modules.coursesPrice.update.coursesSection.title')"
                        :subtitle="$t('modules.coursesPrice.update.coursesSection.subtitle')" class="mb-6">
                        <TransferList
                            :firstListTitle="$t('modules.coursesPrice.update.coursesSection.transferList.firstTitle')"
                            :secondListTitle="$t('modules.coursesPrice.update.coursesSection.transferList.secondTitle')"
                            :availableItems.sync="availableCourses" :addedItems.sync="$v.selectedCourses.$model">
                        </TransferList>
                        <v-alert text type="error" class="mt-4"
                            v-if="$v.selectedCourses.$dirty && $v.selectedCourses.$anyError">{{
                                $t('modules.coursesPrice.validations.noSelectedCourses') }}
                        </v-alert>
                    </OutlinedCard>
                    <OutlinedCard :title="$t('modules.coursesPrice.update.careersSection.title')"
                        :subtitle="$t('modules.coursesPrice.update.careersSection.subtitle')" class="mb-6">
                        <TransferList
                            :firstListTitle="$t('modules.coursesPrice.update.careersSection.transferList.firstTitle')"
                            :secondListTitle="$t('modules.coursesPrice.update.careersSection.transferList.secondTitle')"
                            :availableItems.sync="availableCareers" :addedItems.sync="$v.selectedCareers.$model">
                        </TransferList>
                        <v-alert text type="error" class="mt-4"
                            v-if="$v.selectedCareers.$dirty && $v.selectedCareers.$anyError">{{
                                $t('modules.coursesPrice.validations.noSelectedCareers') }}
                        </v-alert>
                    </OutlinedCard>
                    <OutlinedCard :title="$t('modules.coursesPrice.update.termSection.title')"
                        :subtitle="$t('modules.coursesPrice.update.termSection.subtitle')" class="mb-6">
                        <TransferList :firstListTitle="$t('modules.coursesPrice.update.termSection.transferList.firstTitle')"
                            :secondListTitle="$t('modules.coursesPrice.update.termSection.transferList.secondTitle')"
                            :availableItems.sync="terms" :added-items.sync="$v.termsAdded.$model" :maxSelected="1"></TransferList>
                        <v-alert text type="error" class="mt-4"
                            v-if="$v.termsAdded.$dirty && $v.termsAdded.$anyError">{{
                                $t('modules.coursesPrice.validations.noSelectedTerms') }}
                        </v-alert>
                    </OutlinedCard>
                    <OutlinedCard :title="$t('modules.coursesPrice.update.detailsSection.title')" class="mb-6">
                        <UpdateDetailsTable :headers="detailsHeaders" :details="$v.details.$each.$iter"></UpdateDetailsTable>
                    </OutlinedCard>

                    <div class="d-flex justify-end">
                        <Button white :text="$t('actions.cancel')" @clicked="$router.replace('/courses')"
                            @end="resetButtonValues" class="mr-4"></Button>
                        <Button :loading="updateButton.loading" :success="updateButton.success"
                            :error="updateButton.error" :text="$t('actions.save')" :successText="$t('actions.updated')"
                            :errorText="$t('actions.error')" :disabled="!canUpdate || $v.$invalid || !$v.selectedBillingProduct.$model || !$v.selectedCourses.$model || !$v.selectedCareers.$model || !$v.termsAdded.$model" @end="resetButtonValues"
                            @clicked="updateCourse"></Button>
                    </div>
                </v-col>
            </v-row>
        </v-container>
        <SuccessMessage v-else :type="successMessage.type" :title="successMessage.title"
            :actionPrimary="successMessage.actionPrimary" :actionSecondary="successMessage.actionSecondary" />
    </v-container>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import OutlinedCard from '@/components/OutlinedCard/OutlinedCard';
import Button from '@/components/Button/Button';
import SuccessMessage from '@/components/SuccessMessage/SuccessMessage';
import TransferList from '@/components/TransferList/TransferListNew';
import UpdateDetailsTable from '../Components/UpdateDetailsTable';
import { required } from 'vuelidate/lib/validators';
import { $terms, $careers, $course, $extracurricularCourses, $products } from '../Services';

export default {
    name: 'CourseUpdate',
    components: {
        Breadcrumbs,
        OutlinedCard,
        Button,
        SuccessMessage,
        TransferList,
        UpdateDetailsTable
    },
    data() {
        return {
            availableCareers: [],
            selectedCareers: [],
            availableCourses: [],
            selectedCourses: [],
            terms: [],
            pricesId: null,
            termsAdded: [],
            details: [],
            detailsHeaders: [
                {
                    text: this.$t('modules.coursesPrice.update.detailsSection.table.headers.modalityType'),
                    value: 'model.modalityType.meaning',
                },
                {
                    text: this.$t('modules.coursesPrice.update.detailsSection.table.headers.studentType'),
                    value: 'model.studentType.meaning',
                },
                {
                    text: this.$t('modules.coursesPrice.update.detailsSection.table.headers.zone'),
                    value: 'model.zone.zoneDescription'
                },
                {
                    text: this.$t('modules.coursesPrice.update.detailsSection.table.headers.price'),
                    value: 'amount',
                }
            ],
            status: true,
            updateCourseStatus: false,
            updateButton: {
                loading: false,
                success: false,
                error: false
            },
            successMessage: {
                type: null,
                title: null,
                actionPrimary: null,
                actionSecondary: null
            },
            billingProducts: [],
            selectedBillingProduct: {}
        };
    },
    computed: {
        canUpdate() {
            return this.$permissions.portaladministrativo.academic.courses.UPDATE;
        },
        formattedCoursesStatus() {
            return this.status ? this.$t('modules.coursesPrice.update.section_product.enabled') : this.$t('modules.coursesPrice.update.section_product.disabled')
        },
        filteredBillingProducts() {
            return this.billingProducts.filter(product => product.id === 248);
        }
    },
    async mounted() {
        await this.getCourse()
        await this.getCareers()
        await this.getTerms()
        await this.getBillingProducts()
        this.pricesId = this.$route.params.id;
        if (this.pricesId) {
            await this.fetchCourse(this.pricesId)
        }
    },
    methods: {
        async fetchCourse(pricesId) {
            try {
                const { data } = await $course.find(`prices/${pricesId}`, null, null);
                this.status = data.status;
                
                if (data.billingProducts) {
                    this.selectedBillingProduct = data.billingProducts;
                }
                
                this.selectedCareers = data.careers?.map(item => ({
                    code: item.code,
                    description: item.description,
                    id: item.id,
                    name: `(${item.code}) ${item.description}`
                })) || [];

                this.selectedCourses = data.academicCourseIds?.map(courseId => {
                    const courseData = this.availableCourses.find(course => course.id === courseId);
                    return courseData ? {
                        code: courseData.code,
                        description: courseData.description,
                        id: courseData.id,
                        name: courseData.name
                    } : null;
                }).filter(course => course !== null) || [];

                if (data.priceDetails && data.priceDetails.length > 0) {
                    const termIds = data.priceDetails.map(d => d.termId).filter(id => id != null);
                    const uniqueTermIds = [...new Set(termIds)];
                    this.termsAdded = uniqueTermIds.map(id => this.terms.find(t => t.id === id)).filter(t => t);
                }

                this.details = data.priceDetails || [];

                this.removeSelectedCareersFromAvailable();
                this.removeSelectedCoursesFromAvailable();
            } catch (error) {
                this.$store.dispatch('commons/openPopUp', {
                    title: this.$t('alert'),
                    content: [{ value: this.$t('modules.coursesPrice.update.errorMessage.priceConfigurationError') }],
                    actionPrimary: { text: this.$t('actions.accept'), callback() {} },
                    icon: { color: 'warning', name: 'mdi-alert' },
                    color: 'primary',
                });
            }
        },
        removeSelectedCareersFromAvailable() {
            if (this.selectedCareers.length > 0) {
                const selectedIds = this.selectedCareers.map(career => career.id);
                this.availableCareers = this.availableCareers.filter(career => 
                    !selectedIds.includes(career.id)
                );
            }
        },
        removeSelectedCoursesFromAvailable() {
            if (this.selectedCourses.length > 0) {
                const selectedIds = this.selectedCourses.map(course => course.id);
                this.availableCourses = this.availableCourses.filter(course => 
                    !selectedIds.includes(course.id)
                );
            }
        },
        async getTerms() {
            try {
                const { data } = await $terms.find(null, null, {
                params: {
                    page: 0,
                    length: 100,
                    orderBy: 'classStartDate',
                    orientation: 'desc',
                }
                });
                this.terms = data.content
                    .filter(item => item.billingProducts && item.billingProducts.some(p => p.id === 248))
                    .map(item => {
                    item.name = `(${item.code}) ${item.description}`;
                    return item;
                });
            } catch (error) {
                this.terms = [];
            }
            },
        async getCourse() {
            try {
                const { data } = await $extracurricularCourses.find(null, null, { params: { page: null, length: null, orderBy: 'id', orientation: 'desc' } });
                this.availableCourses = data.content.map(item => ({
                    code: item.code,
                    description: item.legalDescription,
                    id: item.id,
                    name: `(${item.code}) ${item.name}`
                }));
            } catch (error) {
                this.availableCourses = [];
            }
        },
        async getCareers() {
            try {
                const { data } = await $careers.find(null, null, {
                    params: {
                        page: 0,
                        length: 100,
                        orderBy: 'id',
                        orientation: 'desc',
                        excludeStudyPlan: true
                    }
                });
                this.availableCareers = data.content.map(item => {
                    item.name = `(${item.code}) ${item.description}`;
                    return item;
                });
            } catch (err) {
                this.availableCareers = [];
                throw err;
            }
        },
        async getBillingProducts() {
            try {
                const { data } = await $products.find(null, null, { params: { page: 0, length: 500, orderBy: 'id', orientation: 'desc' } });
                this.billingProducts = data.content;
            } catch (error) {
                this.billingProducts = [];
            }
        },
        async updateCourse() {
            this.updateButton.loading = true;
            try {
                if (this.$v.$invalid) {
                    this.$v.$touch();
                    this.updateButton.error = true;
                    this.updateButton.loading = false;
                } else {
                    const newCourse = {
                        priceId: this.pricesId,
                        status: this.status,
                        termId: this.termsAdded[0].id,
                        academicCourseIds: this.selectedCourses.map(course => course.id),
                        billingProduct: {
                            id: this.selectedBillingProduct.id
                        },
                        careerIds: this.selectedCareers.map(career => career.id),
                        priceDetails: this.details
                    };
                    await $course.update(`prices/${this.pricesId}`, newCourse);
                    this.successMessage.type = 'success';
                    this.successMessage.title = this.$t('modules.coursesPrice.update.successMessage.title');
                    this.successMessage.actionPrimary = {
                        text: this.$t('actions.close'), callback: () => {
                            this.$router.push('/courses');
                        }
                    };

                    this.updateCourseStatus = true;
                    this.updateButton.success = true;
                    this.updateButton.loading = false;
                    this.clearData();
                }
            } catch (error) {
                this.successMessage.type = 'error';
                this.successMessage.title = (error.codeMeaning != '') ? error.codeMeaning : this.$t('modules.coursesPrice.update.errorMessage.title');
                this.successMessage.actionPrimary = {
                    text: this.$t('actions.return'), callback: () => {
                        this.updateCourseStatus = false;
                    }
                };
                this.successMessage.actionSecondary = null;
                this.updateCourseStatus = true;
                this.updateButton.loading = false;
                this.updateButton.error = true;
                throw error;
            }
        },
        clearData() {
            this.resetButtonValues();
            this.status = true;
            this.selectedBillingProduct = {};
            this.termsAdded = [];
            this.selectedCareers = [];
            this.selectedCourses = [];
            this.$v.$reset();
        },
        resetButtonValues() {
            this.updateButton.loading = false;
            this.updateButton.success = false;
            this.updateButton.error = false;
        },
    },
    validations: {
        selectedBillingProduct: { required },
        termsAdded: { required },
        selectedCareers: { required },
        selectedCourses: { required },
        details: {
            $each: { 
                amount: { required } 
            }
        }
    },
    watch: {
        selectedBillingProduct: {
            handler() {
                this.$v.selectedBillingProduct.$touch();
            },
            deep: true
        },
        selectedCourses: {
            handler() {
                this.$v.selectedCourses.$touch();
            },
            deep: true
        },
        selectedCareers: {
            handler() {
                this.$v.selectedCareers.$touch();
            },
            deep: true
        },
        termsAdded: {
            handler() {
                this.$v.termsAdded.$touch();
            },
            deep: true
        }
    }
};
</script>